import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import BottomNav from "../components/BottomNav";
import { ArrowLeft, TrendingUp, TrendingDown, Minus, AlertTriangle } from "lucide-react";

const FLAG_LABELS = {
  manipulation: "Manipulace",
  ghosting: "Ghosting",
  love_bombing: "Love bombing",
  narcissism: "Narcissus",
  gaslighting: "Gaslighting",
  lying: "Lhaní",
  breadcrumbing: "Breadcrumbing",
  stonewalling: "Stonewalling",
  other: "Toxický vzorec",
};

const TREND = {
  improving: { icon: TrendingUp, label: "Lepší se", color: "text-emerald-600" },
  declining: { icon: TrendingDown, label: "Zhoršuje se", color: "text-rose-600" },
  stable: { icon: Minus, label: "Stabilní", color: "text-[#5F7F77]" },
  single: { icon: Minus, label: "Jediná analýza", color: "text-[#7A8784]" },
};

export default function Timeline() {
  const nav = useNavigate();
  const [groups, setGroups] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/timeline"); setGroups(data); }
      catch { setGroups([]); }
    })();
  }, []);

  return (
    <div className="app-shell bg-aurora min-h-screen pb-nav" data-testid="timeline-page">
      <div className="px-6 pt-10 pb-5 flex items-center gap-3">
        <button onClick={() => nav("/home")} className="text-[#7A8784]"><ArrowLeft size={20} strokeWidth={1.7} /></button>
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[#E85D04] font-medium">Sentra AI</p>
          <h1 className="font-display text-3xl text-[#1A2924]">Red Flag Timeline</h1>
        </div>
      </div>

      <div className="px-6">
        {groups === null && [...Array(2)].map((_, i) => <div key={i} className="h-32 rounded-3xl shimmer mb-3" />)}
        {groups?.length === 0 && (
          <div className="glass-card p-10 text-center text-[#7A8784]" data-testid="no-timeline">
            <AlertTriangle size={36} className="mx-auto mb-3 text-[#5F7F77]" strokeWidth={1.5} />
            <p>Zatím nemáš dost analýz.<br />Analyzuj 2+ konverzace se stejnou osobou a uvidíš vývoj. 💚</p>
          </div>
        )}

        <div className="space-y-4">
          {groups?.map((g) => {
            const T = TREND[g.trend] || TREND.stable;
            const Icon = T.icon;
            const isOpen = expanded === g.name;
            return (
              <div key={g.name} className="glass-card overflow-hidden" data-testid={`timeline-${g.name}`}>
                <button onClick={() => setExpanded(isOpen ? null : g.name)} className="w-full p-5 text-left">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-display text-xl text-[#1A2924]">{g.name}</h3>
                      <p className="text-xs text-[#7A8784] mt-0.5">{g.count} {g.count === 1 ? "analýza" : g.count < 5 ? "analýzy" : "analýz"}</p>
                    </div>
                    <div className={`flex items-center gap-1.5 text-sm ${T.color}`}>
                      <Icon size={16} strokeWidth={1.8} />
                      <span>{T.label}{g.trend_delta !== 0 && ` (${g.trend_delta > 0 ? "+" : ""}${g.trend_delta})`}</span>
                    </div>
                  </div>

                  {/* Mini SVG line chart */}
                  {g.entries.length >= 2 && <MiniChart entries={g.entries} />}

                  <div className="flex items-center justify-between mt-3">
                    <span className="text-xs text-[#7A8784]">{g.latest_icon} {g.latest_verdict}</span>
                    <span className="font-display text-2xl text-[#0F3D34]">{g.latest_score}%</span>
                  </div>

                  {g.all_red_flag_types.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {g.all_red_flag_types.slice(0, 5).map((t) => (
                        <span key={t} className={`pill text-[0.65rem] ${g.new_red_flag_types.includes(t) ? "border-rose-200 text-rose-700 bg-rose-50" : ""}`}>
                          🚩 {FLAG_LABELS[t] || t}
                        </span>
                      ))}
                    </div>
                  )}
                </button>

                {isOpen && (
                  <div className="border-t border-[#E5DDD0] bg-[#FAF5EC]/50 p-5 space-y-3" data-testid={`expanded-${g.name}`}>
                    {g.entries.map((e, i) => (
                      <Link key={e.id} to={`/analyses/${e.id}`} className="block bg-white rounded-2xl p-3 border border-[#E5DDD0]">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-[#7A8784]">{new Date(e.date).toLocaleDateString("cs-CZ", { day: "numeric", month: "long", year: "numeric" })}</span>
                          <span className="font-display text-lg text-[#0F3D34]">{e.overall_score}%</span>
                        </div>
                        <p className="text-sm text-[#1A2924]">{e.verdict_icon} {e.verdict_headline}</p>
                        {e.red_flag_count > 0 && <p className="text-xs text-rose-600 mt-1">🚩 {e.red_flag_count} red flag{e.red_flag_count > 1 ? "ů" : ""}</p>}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <BottomNav />
    </div>
  );
}

function MiniChart({ entries }) {
  const W = 320, H = 60, P = 4;
  const max = 100;
  const xs = entries.map((_, i) => P + (i * (W - 2 * P) / Math.max(1, entries.length - 1)));
  const ys = entries.map((e) => P + (1 - e.overall_score / max) * (H - 2 * P));
  const points = xs.map((x, i) => `${x},${ys[i]}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-12 mt-1">
      <defs>
        <linearGradient id="lineGrad" x1="0" x2="1">
          <stop offset="0%" stopColor="#082A24" />
          <stop offset="100%" stopColor="#5F7F77" />
        </linearGradient>
      </defs>
      <polyline points={points} fill="none" stroke="url(#lineGrad)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {xs.map((x, i) => (
        <circle key={entries[i].id ?? `pt-${i}`} cx={x} cy={ys[i]} r={3} fill={entries[i].red_flag_count > 0 ? "#E11D48" : "#0F3D34"} />
      ))}
    </svg>
  );
}
